<template>
  <div class="w-full bg-gray-50 h-screen flex flex-col">
    <!-- Header -->
    <div class="bg-white border-b px-6 py-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <button @click="goBack" class="text-gray-600 hover:text-gray-800">
            <font-awesome-icon icon="arrow-left" class="mr-2" />
            Zurück zur Tabelle
          </button>
          <h1 class="text-2xl font-bold">
            {{ pollId ? (group.Titel || `Umfrage #${pollId}`) + ' bearbeiten' : 'Neue Umfrage anlegen' }}
          </h1>
        </div>
        <div class="flex gap-2">
          <button v-if="pollId" @click="copyPoll" :disabled="loading || saving"
            class="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50">
            <font-awesome-icon icon="copy" class="mr-2" />
            Kopieren
          </button>
          <button v-if="pollId" @click="savePoll" :disabled="loading || saving || !hasChanges"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            <font-awesome-icon icon="save" class="mr-2" />
            Speichern
          </button>
          <button @click="saveAndClose" :disabled="loading || saving"
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
            <font-awesome-icon icon="save" class="mr-2" />
            Speichern und schließen
          </button>
          <button v-if="pollId" @click="exportResults" :disabled="loading"
            class="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">
            <font-awesome-icon icon="file-export" class="mr-2" />
            Ergebnisse exportieren
          </button>
          <button v-if="Umfragegruppe && Umfragegruppe !== 0" @click="deleteGroup" :disabled="loading || saving"
            class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
            <font-awesome-icon icon="trash" class="mr-2" />
            Gruppe löschen
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto p-6">
      <div class="max-w-5xl mx-auto space-y-6">
        <!-- Poll Link Card -->
        <div v-if="pollId" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <label class="block text-sm font-medium text-blue-900 mb-1">Umfrage-Link</label>
              <div class="flex items-center gap-2">
                <input 
                  :value="pollLink" 
                  readonly 
                  class="flex-1 border border-blue-300 rounded px-3 py-2 bg-white font-mono text-sm"
                />
                <button 
                  @click="copyPollLink" 
                  class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                  :title="'Link kopieren'"
                >
                  <font-awesome-icon :icon="copied ? 'check' : 'copy'" />
                  {{ copied ? 'Kopiert!' : 'Kopieren' }}
                </button>
              </div>
              <p class="text-xs text-blue-700 mt-1">
                Teilen Sie diesen Link, um Kunden direkten Zugriff auf die Umfrage zu geben.
              </p>
            </div>
          </div>
        </div>
        
        <!-- Not Saved Hint -->
        <div v-else class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div class="flex items-center gap-2">
            <font-awesome-icon icon="exclamation-triangle" class="text-yellow-600" />
            <p class="text-sm text-yellow-800">
              <strong>Hinweis:</strong> Speichern Sie die Umfrage, um einen teilbaren Link zu erhalten.
            </p>
          </div>
        </div>

        <!-- Group Settings Card -->
        <div class="bg-white rounded-lg shadow p-6">
          <h2 class="text-xl font-semibold mb-6">Gruppeneinstellungen</h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
              <input v-model="group.Titel" type="text" class="w-full border rounded px-3 py-2" placeholder="Titel der Umfragegruppe" required />
              <p class="text-xs text-gray-500 mt-1">Dieser Titel wird als Titel der ersten Frage verwendet.</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Projekt</label>
              <select v-model="group.Projekt" class="w-full border rounded px-3 py-2">
                <option :value="null">Kein Projekt</option>
                <option v-for="project in projects" :key="project.id" :value="project.id">
                  {{ project.name }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Startdatum</label>
              <input v-model="group.Startdatum" type="datetime-local" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Enddatum</label>
              <input v-model="group.Enddatum" type="datetime-local" class="w-full border rounded px-3 py-2" />
            </div>

            <div class="flex items-center gap-2 pt-4">
              <input v-model="group.Aktiv" type="checkbox" id="aktiv" class="small" />
              <label for="aktiv">Aktiv</label>
            </div>
          </div>
        </div>

        <!-- Questions Card -->
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold">Fragen</h2>
              <p class="text-sm text-gray-600 mt-1">Fügen Sie mehrere Fragen hinzu, um eine Umfragegruppe zu erstellen.</p>
            </div>
            <button @click="addQuestion" class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              <font-awesome-icon icon="plus" class="mr-2" />
              Frage hinzufügen
            </button>
          </div>

          <div class="space-y-6">
            <div v-for="(question, qIndex) in questions" :key="question.id || `new-${qIndex}`" 
              class="border rounded-lg p-4 bg-gray-50">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold">Frage {{ qIndex + 1 }}</h3>
                <button
                  @click="deleteQuestion(question, qIndex)"
                  class="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                  :title="'Frage löschen'"
                >
                  <font-awesome-icon icon="times-circle" />
                </button>
              </div>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                  <input v-model="question.Titel" type="text" class="w-full border rounded px-3 py-2" required />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                  <textarea v-model="question.Beschreibung" rows="2" class="w-full border rounded px-3 py-2"></textarea>
                </div>

                <!-- Question Settings -->
                <div class="flex flex-wrap gap-4">
                  <div class="flex items-center gap-2">
                    <input v-model="question.Mehrfachauswahl" type="checkbox" :id="`mehrfach-${qIndex}`" class="small" />
                    <label :for="`mehrfach-${qIndex}`">Mehrfachauswahl</label>
                  </div>
                  <div class="flex items-center gap-2">
                    <input v-model="question.Kommentare" type="checkbox" :id="`kommentare-${qIndex}`" class="small" />
                    <label :for="`kommentare-${qIndex}`">Kommentare</label>
                  </div>
                  <div class="flex items-center gap-2">
                    <input v-model="question.FreitextErlaubt" type="checkbox" :id="`freitext-${qIndex}`" class="small" @change="handleFreitextErlaubtChange(question)" />
                    <label :for="`freitext-${qIndex}`">Freitext erlauben</label>
                  </div>
                </div>

                <!-- Answer Options -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <label class="block text-sm font-medium text-gray-700">Antwortoptionen</label>
                    <button @click="addOption(question)" class="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700">
                      <font-awesome-icon icon="plus" class="mr-1" />
                      Option
                    </button>
                  </div>
                  <p v-if="question.id" class="text-xs text-gray-600 mb-2">Bisher eingegangen: {{ question.answer_count || 0 }} Antworten</p>
                  
                  <div class="space-y-2">
                    <div v-for="(option, oIndex) in question.options" :key="option.id || `new-${oIndex}`" 
                      class="flex items-center gap-2 p-2 border rounded bg-white hover:bg-gray-50">
                      <div class="flex-1">
                        <input 
                          v-model="option.Antwort" 
                          type="text" 
                          placeholder="Antworttext"
                          class="w-full border rounded px-2 py-1 text-sm" :class="option.isFreetextOption?'bg-gray-200':''"
                          :readonly="option.isFreetextOption"
                          @blur="question.id && !option.isFreetextOption ? saveOption(question, option) : null"
                        />
                      </div>
                      <button 
                        v-if="!option.isFreetextOption"
                        @click="deleteOption(question, option, oIndex)" 
                        class="px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                        :disabled="deletingOption === option.id"
                      >
                        <font-awesome-icon icon="times" />
                      </button>
                    </div>
                    <div v-if="question.options.length === 0" class="text-center text-gray-500 py-4 text-sm">
                      Keine Antwortoptionen vorhanden.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="questions.length === 0" class="text-center text-gray-500 py-8">
              Noch keine Fragen vorhanden. Klicken Sie auf "Frage hinzufügen", um zu beginnen.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6">
        <div class="text-lg font-semibold">Lade...</div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/API'

export default {
  name: 'PollEdit',
  props: {
    user: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      pollId: null,
      Umfragegruppe: 0,
      originalGroup: {},
      group: {
        Titel: '',
        Projekt: null,
        Startdatum: '',
        Enddatum: '',
        Aktiv: true
      },
      questions: [],
      questionsToDelete: [],
      projects: [],
      deletingOption: null,
      copied: false,
      isCopy: false
    }
  },
  computed: {
    hasChanges() {
      return JSON.stringify(this.group) !== JSON.stringify(this.originalGroup) ||
             this.questionsToDelete.length > 0 ||
             this.questions.some(q => q.isNew || q.isModified)
    },
    pollLink() {
      if (!this.pollId) return ''
      const meinUrl = process.env.VUE_APP_MEIN_URL || 'https://mein.ev-angel-isch.de'
      return `${meinUrl}/services?pollId=${this.pollId}`
    }
  },
  async created() {
    const id = this.$route.params.id || this.$route.query.id
    const copyFrom = this.$route.query.copyFrom

    if (copyFrom) {
      // Load poll to copy from
      await this.loadPollForCopy(parseInt(copyFrom))
    } else if (id && id !== 'new') {
      this.pollId = parseInt(id)
      await this.loadPoll()
    }
    await this.loadProjects()
  },
  methods: {
    handleFreitextErlaubtChange(question) {
      const freitextOption = question.options.find(opt => opt.isFreetextOption)
      
      if (question.FreitextErlaubt && !freitextOption) {
        // Add FREITEXT option
        question.options.push({
          Antwort: 'Freitext',
          Freitext: true,
          isFreetextOption: true,
          isNew: true
        })
        question.isModified = true
      } else if (!question.FreitextErlaubt && freitextOption) {
        // Remove FREITEXT option
        const index = question.options.indexOf(freitextOption)
        if (index > -1) {
          question.options.splice(index, 1)
          question.isModified = true
        }
      }
    },

    async loadPollForCopy(groupId) {
      this.loading = true
      this.isCopy = true
      try {
        // Load the group data
        const groupData = await new Promise((resolve, reject) => {
          API.getUmfragegruppe(groupId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error(`Failed to load poll group: ${status}`))
            }
          })
        })

        // Load all polls in the group
        const pollsToCopy = await new Promise((resolve, reject) => {
          API.getPolls({ Umfragegruppe: groupId }, (data, status) => {
            if (status === 200) {
              resolve(data.rows || data)
            } else {
              reject(new Error(`Failed to load group polls: ${status}`))
            }
          })
        }).then(polls => polls.sort((a, b) => a.id - b.id))

        // Set group settings from group data
        this.group = {
          Titel: groupData.Titel + ' (Kopie)',
          Projekt: groupData.Projekt,
          Startdatum: groupData.Startdatum ? this.convertToLocalDatetime(groupData.Startdatum) : '',
          Enddatum: groupData.Enddatum ? this.convertToLocalDatetime(groupData.Enddatum) : '',
          Aktiv: groupData.Aktiv
        }
        this.originalGroup = JSON.parse(JSON.stringify(this.group))

        // Convert polls to questions
        this.questions = pollsToCopy.map(p => ({
          id: null,
          Titel: p.Titel,
          Beschreibung: p.Beschreibung,
          Mehrfachauswahl: p.Mehrfachauswahl,
          Kommentare: p.Kommentare,
          FreitextErlaubt: p.FreitextErlaubt,
          answer_count: 0,
          options: p.options ? p.options.map(opt => ({
            Antwort: opt.Antwort,
            Freitext: opt.Freitext || false,
            isFreetextOption: !!opt.Freitext,
            isNew: true
          })) : [],
          isNew: true,
          isModified: false
        }))

        // Clear the copyFrom query parameter
        this.$router.replace({ query: {} })

      } catch (error) {
        console.error('Error loading poll for copy:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Laden der Umfrage zum Kopieren',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.loading = false
      }
    },

    async loadPoll() {
      if (!this.pollId) return

      this.loading = true
      try {
        // Load Umfragegruppe data
        const groupData = await new Promise((resolve, reject) => {
          API.getUmfragegruppe(this.pollId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error(`Failed to load poll group: ${status}`))
            }
          })
        })

        // Set group settings from Umfragegruppe
        this.group = {
          Titel: groupData.Titel,
          Projekt: groupData.Projekt,
          Startdatum: groupData.Startdatum ? this.convertToLocalDatetime(groupData.Startdatum) : '',
          Enddatum: groupData.Enddatum ? this.convertToLocalDatetime(groupData.Enddatum) : '',
          Aktiv: groupData.Aktiv
        }
        this.Umfragegruppe = groupData.id
        this.originalGroup = JSON.parse(JSON.stringify(this.group))

        // Load all polls in the group
        const polls = await new Promise((resolve, reject) => {
          API.getPolls({ Umfragegruppe: this.pollId }, (data, status) => {
            if (status === 200) {
              resolve(data.rows || data)
            } else {
              reject(new Error(`Failed to load group polls: ${status}`))
            }
          })
        }).then(polls => polls.sort((a, b) => a.id - b.id))

        // Convert polls to questions
        this.questions = polls.map(p => ({
          id: p.id,
          Titel: p.Titel,
          Beschreibung: p.Beschreibung,
          Mehrfachauswahl: p.Mehrfachauswahl,
          Kommentare: p.Kommentare,
          FreitextErlaubt: p.FreitextErlaubt,
          answer_count: p.answer_count || 0,
          options: (p.options || []).map(opt => ({
            ...opt,
            isFreetextOption: !!opt.Freitext
          })),
          isNew: false,
          isModified: false
        }))

      } catch (error) {
        console.error('Error loading poll:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Laden der Umfrage',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.loading = false
      }
    },

    async loadProjects() {
      try {
        const projects = await new Promise((resolve, reject) => {
          API.getProjects((data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load projects'))
            }
          })
        })
        this.projects = projects
      } catch (error) {
        console.error('Error loading projects:', error)
      }
    },

    async savePoll() {
      this.saving = true
      try {
        // Convert datetime-local to ISO format
        const groupData = { ...this.group }
        if (groupData.Startdatum) {
          groupData.Startdatum = this.convertToISO(groupData.Startdatum)
        }
        if (groupData.Enddatum) {
          groupData.Enddatum = this.convertToISO(groupData.Enddatum)
        }

        // If creating a new group (no Umfragegruppe), use the new endpoint
        if (!this.Umfragegruppe || this.Umfragegruppe === 0) {
          const pollsData = this.questions.map(q => ({
            Titel: q.Titel,
            Beschreibung: q.Beschreibung,
            Mehrfachauswahl: q.Mehrfachauswahl,
            Kommentare: q.Kommentare,
            FreitextErlaubt: q.FreitextErlaubt,
            options: q.isNew && q.options.length > 0 ? q.options.map(opt => ({
              Antwort: opt.Antwort,
              Freitext: opt.Freitext || false
            })) : []
          }))

          const response = await new Promise((resolve, reject) => {
            API.createPollGroup({
              ...groupData,
              polls: pollsData
            }, (data, status) => {
              if (status === 201) {
                resolve(data)
              } else {
                reject(new Error('Failed to create poll group'))
              }
            })
          })

          this.Umfragegruppe = response.groupId
          this.pollId = response.groupId
        } else {
          // Update existing Umfragegruppe
          await new Promise((resolve, reject) => {
            API.updateUmfragegruppe(this.Umfragegruppe, groupData, (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(new Error(`Failed to update poll group: ${status}`))
              }
            })
          })

          // Update all existing polls
          for (const question of this.questions) {
            const pollData = {
              Umfragegruppe: this.Umfragegruppe,
              Titel: question.Titel,
              Beschreibung: question.Beschreibung,
              Mehrfachauswahl: question.Mehrfachauswahl,
              Kommentare: question.Kommentare,
              FreitextErlaubt: question.FreitextErlaubt,
              Aktiv: groupData.Aktiv,
              Startdatum: groupData.Startdatum,
              Enddatum: groupData.Enddatum
            }

            // Include group fields for backwards compatibility
            if (groupData.Projekt) {
              pollData.Projekt = groupData.Projekt
            }

            // Include options for new questions
            if (question.isNew && question.options.length > 0) {
              pollData.options = question.options.map(opt => ({
                Antwort: opt.Antwort,
                Freitext: opt.Freitext || false
              }))
            }

            if (question.id) {
              // Update existing question
              await new Promise((resolve, reject) => {
                API.updatePoll(question.id, pollData, (data, status) => {
                  if (status === 200) {
                    resolve(data)
                  } else {
                    reject(new Error('Failed to update poll'))
                  }
                })
              })
            } else {
              // Create new question for existing group
              await new Promise((resolve, reject) => {
                API.createPoll(pollData, (data, status) => {
                  if (status === 201) {
                    resolve(data)
                  } else {
                    reject(new Error('Failed to create poll'))
                  }
                })
              })
            }
          }
        }

        // Delete queued questions
        for (const questionId of this.questionsToDelete) {
          await new Promise((resolve, reject) => {
            API.deletePoll(questionId, (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(new Error('Failed to delete poll'))
              }
            })
          })
        }
        this.questionsToDelete = []

        // Reload to get fresh data
        if (this.pollId) {
          await this.loadPoll()
        }

        this.$toast?.open?.({
          message: 'Umfrage gespeichert',
          type: 'success',
          position: 'top'
        })
        return true

      } catch (error) {
        console.error('Error saving poll:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Speichern',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.saving = false
      }
      return false
    },

    async saveAndClose() {
      if (await this.savePoll()) {
        this.goBack()
      }
    },

    addQuestion() {
      this.questions.push({
        id: null,
        Titel: '',
        Beschreibung: '',
        Mehrfachauswahl: false,
        Kommentare: false,
        FreitextErlaubt: false,
        answer_count: 0,
        options: [],
        isNew: true,
        isModified: false
      })
    },

    deleteQuestion(question, index) {
      if (!confirm('Möchten Sie diese Frage wirklich löschen?')) return

      if (question.id) {
        // Queue for deletion
        this.questionsToDelete.push(question.id)
      }
      this.questions.splice(index, 1)
    },

    addOption(question) {
      question.options.push({
        Antwort: '',
        Freitext: false,
        isNew: true
      })
      question.isModified = true
    },

    async saveOption(question, option) {
      // Only save to server if question already exists and option is not the freetext option
      if (!question.id || !option.Antwort || option.isFreetextOption) return

      try {
        if (option.isNew || !option.id) {
          // Create new option
          const response = await new Promise((resolve, reject) => {
            API.createPollOption(question.id, {
              Antwort: option.Antwort,
              Freitext: option.Freitext || false
            }, (data, status) => {
              if (status === 201) {
                resolve(data)
              } else {
                reject(new Error('Failed to create option'))
              }
            })
          })
          
          // Update the option with the ID from server
          Object.assign(option, response)
          delete option.isNew
        } else {
          // Update existing option
          await new Promise((resolve, reject) => {
            API.updatePollOption(question.id, option.id, {
              Antwort: option.Antwort,
              Freitext: option.Freitext || false
            }, (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(new Error('Failed to update option'))
              }
            })
          })
        }
      } catch (error) {
        console.error('Error saving option:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Speichern der Option',
          type: 'error',
          position: 'top'
        })
      }
    },

    async deleteOption(question, option, index) {
      if (option.isFreetextOption) return // Prevent deletion of freetext option
      if (!confirm('Möchten Sie diese Antwortoption wirklich löschen?')) return

      if (!option.id) {
        // Just remove from array if not saved yet
        question.options.splice(index, 1)
        return
      }

      this.deletingOption = option.id
      try {
        await new Promise((resolve, reject) => {
          API.deletePollOption(question.id, option.id, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to delete option'))
            }
          })
        })

        question.options.splice(index, 1)
        
        this.$toast?.open?.({
          message: 'Option gelöscht',
          type: 'success',
          position: 'top'
        })
      } catch (error) {
        console.error('Error deleting option:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Löschen der Option',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.deletingOption = null
      }
    },

    exportResults() {
      if (!this.pollId) return
      const url = `${process.env.VUE_APP_BE_URL}/polls/${this.pollId}/export?group=${this.Umfragegruppe || ''}`
      window.open(url, '_blank')
    },

    convertToLocalDatetime(isoString) {
      if (!isoString) return ''
      const date = new Date(isoString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },

    convertToISO(localDatetime) {
      if (!localDatetime) return null
      return new Date(localDatetime).toISOString()
    },

    async copyPollLink() {
      try {
        await navigator.clipboard.writeText(this.pollLink)
        this.copied = true
        this.$toast?.open?.({
          message: 'Link in Zwischenablage kopiert',
          type: 'success',
          position: 'top'
        })
        setTimeout(() => {
          this.copied = false
        }, 2000)
      } catch (error) {
        console.error('Error copying to clipboard:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Kopieren',
          type: 'error',
          position: 'top'
        })
      }
    },

    goBack() {
      const referrer = document.cookie.split('; ').find(row => row.startsWith('tableReferrer='))
      if (referrer) {
        const url = referrer.split('=')[1]
        window.location.href = decodeURIComponent(url)
      } else {
        this.$router.push('/polls')
      }
    },
    
    async copyPoll() {
      if (!this.pollId) return
      window.location.href = `/polls/new?copyFrom=${this.pollId}`
    },

    async deleteGroup() {
      if (!this.Umfragegruppe || this.Umfragegruppe === 0) return
      if (!confirm('Möchten Sie diese Umfragegruppe und alle enthaltenen Umfragen wirklich löschen?')) return

      this.loading = true
      try {
        await new Promise((resolve, reject) => {
          API.deleteUmfragegruppe(this.Umfragegruppe, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to delete poll group'))
            }
          })
        })

        this.$toast?.open?.({
          message: 'Umfragegruppe gelöscht',
          type: 'success',
          position: 'top'
        })

        this.goBack()
      } catch (error) {
        console.error('Error deleting poll group:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Löschen der Umfragegruppe',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
</style>
